import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface NewDealFooterProps {
  step: number;
  onBack: () => void;
  onContinue: () => void;
  /** Create-deal request in flight. Disables the submit so a second click
   *  cannot open a second deal. */
  busy?: boolean;
  /** Set when Create must wait on a fix (e.g. an address typed as the client
   *  name); shown beside the disabled button. */
  blockedReason?: string;
}

export default function NewDealFooter({ step, onBack, onContinue, busy = false, blockedReason }: NewDealFooterProps) {
  const isLast = step === 3;
  // Every step is a validated form now — step 2 gained a conditional address
  // rule, so "Continue" must run its resolver rather than blindly advancing.
  const usesForm = true;

  return (
    <footer className="relative z-10 grid min-h-20 shrink-0 grid-cols-2 items-center gap-3 border-t border-dash-borderSoft bg-dash-page px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 shadow-[0_-8px_24px_rgba(32,35,31,0.04)] sm:flex sm:h-20 sm:justify-between sm:px-8 sm:py-0 sm:shadow-none md:px-10">
      <Button type="button" variant="dashboardOutline" onClick={onBack} disabled={busy} className="w-full sm:w-auto" data-testid="new-deal-cancel">
        {step === 1 ? "Cancel" : "Back"}
      </Button>
      <div className="flex min-w-0 items-center justify-end gap-3">
      {blockedReason && <p className="hidden text-xs text-dash-faint sm:block" data-testid="new-deal-blocked-reason">{blockedReason}</p>}
      <Button
        type={usesForm ? "submit" : "button"}
        disabled={Boolean(blockedReason)}
        form={usesForm ? "new-deal-step-form" : undefined}
        variant="dashboardPrimary"
        onClick={usesForm ? undefined : onContinue}
        loading={isLast && busy}
        className="w-full min-w-0 sm:w-auto sm:min-w-32"
        data-testid={isLast ? "new-deal-submit" : "new-deal-continue"}
      >
        {isLast ? "Create deal" : "Continue"}<ArrowRight data-icon="inline-end" />
      </Button>
      </div>
    </footer>
  );
}
